import React, { FC } from 'react';
import styled from 'styled-components';
import VisibilityIcon from '@material-ui/icons/Visibility';
import { format } from 'date-fns';
import { useSelector } from 'react-redux';
import { IconWrapper } from './InvoicesItems';
import SwitchCheckbox from '../../Common/Controls/SwitchCheckbox';
import { FeeItemType } from '../../../types';
import { RootState } from '../../../../../redux/root';
import { getInvoiceBalance } from '../../../../../Components/InvoiceTool/InvoiceUtil';

type StatusTone = 'success' | 'warning' | 'info' | 'neutral' | 'danger';

const tonePalette: Record<StatusTone, { bg: string; fg: string; border: string }> = {
  success: { bg: '#E8F6EE', fg: '#196A3B', border: '#C5E7D2' },
  warning: { bg: '#FFF6E0', fg: '#8A5F05', border: '#FFE7AC' },
  info: { bg: '#E8F2FD', fg: '#1E64BF', border: '#CFE6F3' },
  danger: { bg: '#FDECEC', fg: '#9A2523', border: '#F9C9C9' },
  neutral: { bg: '#F2F4F7', fg: '#3B424E', border: '#E6E9EF' },
};

const toneByStatus = (raw: string): StatusTone => {
  const s = (raw || '').trim().toLowerCase();
  if (!s) return 'neutral';
  if (s === 'paid' || s === 'complete' || s === 'completed') return 'success';
  if (s === 'overdue' || s === 'void' || s === 'failed') return 'danger';
  if (s === 'pending' || s === 'open' || s === 'unpaid') return 'warning';
  if (s.includes('process')) return 'info';
  return 'neutral';
};

type Props = {
  isOdd: boolean;
  key: number;
  fee: FeeItemType;
  setSelectedItem: Function;
};

const MobileRow: FC<Props> = ({ isOdd, fee, setSelectedItem }) => {
  const selectedInvoicesToPay = useSelector((s: RootState) => s.fees.selectedInvoicesToPay);
  const tone = toneByStatus(fee.status);
  const pal = tonePalette[tone];
  const isPaid = fee.status === 'Paid';
  const invoiceNo = (fee.invoiceNumber || '').split('----')[0] || '—';
  const datePaid = fee.paidAt ? format(new Date(fee.paidAt), 'MM/dd/yyyy') : '—';

  return (
    <Wrapper isOdd={isOdd}>
      <ToggleCell>
        {isPaid ? (
          <Spacer />
        ) : (
          <SwitchCheckbox
            name=""
            value={`${selectedInvoicesToPay[fee.id]}`}
            title=""
            onChange={e => setSelectedItem(e, fee.id)}
            isRadio={false}
            label=""
            disabled={false}
            checked={!!selectedInvoicesToPay[fee.id]}
          />
        )}
      </ToggleCell>

      <InvoiceNo>{invoiceNo}</InvoiceNo>

      <Amount>${getInvoiceBalance(fee)}</Amount>

      <StatusChip style={{ background: pal.bg, color: pal.fg, borderColor: pal.border }}>
        {fee.status || '—'}
      </StatusChip>

      <DateText>{datePaid}</DateText>

      <ViewCell>
        <IconWrapper hasPdf>
          {fee.invoicePdf ? (
            <a target="_blank" rel="noreferrer" href={fee.invoicePdf}>
              <VisibilityIcon />
            </a>
          ) : (
            <VisibilityIcon />
          )}
        </IconWrapper>
      </ViewCell>
    </Wrapper>
  );
};

export default MobileRow;

const Wrapper = styled.div<{ isOdd: boolean }>`
  display: none;

  @media only screen and (max-width: 840px) {
    display: grid;
    grid-template-columns: 36px minmax(0, 1fr) auto auto 32px;
    column-gap: 8px;
    align-items: center;
    padding: 8px 10px;
    border-bottom: 1px solid var(--border);
    background: ${({ isOdd }) => (isOdd ? 'var(--neutral-50)' : 'var(--neutral-0)')};
    font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont,
      'Helvetica Neue', Arial, Roboto, sans-serif;
  }

  @media only screen and (max-width: 420px) {
    grid-template-columns: 32px minmax(0, 1fr) auto 28px;
    column-gap: 6px;
    row-gap: 2px;
    padding: 8px;
  }
`;

const ToggleCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
`;

const Spacer = styled.div`
  width: 100%;
  height: 22px;
`;

const InvoiceNo = styled.span`
  font-family: 'SFMono-Regular', Menlo, Consolas, monospace;
  font-size: 0.75rem;
  color: var(--neutral-700);
  background: var(--neutral-100);
  border: 1px solid var(--border);
  padding: 2px 6px;
  border-radius: 5px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
  justify-self: start;
  max-width: 100%;
`;

const Amount = styled.span`
  font-size: 0.875rem;
  font-weight: 700;
  color: var(--success-700, #196a3b);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;

const StatusChip = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  border: 1px solid var(--border);
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  line-height: 1.4;
  white-space: nowrap;
  max-width: 100%;

  @media only screen and (max-width: 420px) {
    display: none;
  }
`;

const DateText = styled.span`
  font-size: 0.75rem;
  color: var(--neutral-600);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;

  @media only screen and (max-width: 420px) {
    display: none;
  }
`;

const ViewCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;
